import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Hourglass, Sparkles, ArrowUpDown, Tags, LayoutGrid, Smartphone } from 'lucide-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import EisenhowerMatrixSVG from './EisenhowerMatrixSVG';
import SignInOverlay from './SignInOverlay';

const LandingPage = ({ onSignIn, onRegister, onForgotPassword }) => {
    const [stats, setStats] = useState({ aiNotesGeneratedCount: 0, hourlySuggestionsCount: 0 });
    const [showSignInModal, setShowSignInModal] = useState(false);
    const [initialTab, setInitialTab] = useState('signin');

    useEffect(() => {
        const fetchStats = async () => {
            const db = getFirestore();
            const statsRef = doc(db, 'stats', 'counters');
            const statsDoc = await getDoc(statsRef);
            if (statsDoc.exists()) {
                setStats(statsDoc.data());
            }
        };

        fetchStats();
    }, []);

    const features = [
        { icon: <LayoutGrid size={32} />, text: "Eisenhower Matrix for effective task prioritization" },
        { icon: <ArrowUpDown size={32} />, text: "Drag-and-drop interface for easy task management" },
        { icon: <Check size={32} />, text: "Subtasks and checklists for detailed task breakdowns" },
        { icon: <Smartphone size={32} />, text: "Real-time syncing across devices" },
        { icon: <Tags size={32} />, text: "Category-based filtering for better organization" },
        { icon: <Sparkles size={32} />, text: "AI Task Planner for quick task creation" },
        { icon: <Hourglass size={32} />, text: "Next Hour feature with AI-powered suggestions" }
    ];

    const buttonStyle = "bg-white text-purple-600 px-6 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors";

    const openModal = (tab) => {
        setInitialTab(tab);
        setShowSignInModal(true);
    };

    return (
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
            <div className="min-h-screen flex flex-col">
                {/* Header */}
                <header className="container mx-auto px-4 py-6">
                    <nav className="flex justify-between items-center relative">
                        <img 
                            src="/AisenHour-Hourglass-Horizontal.png" 
                            alt="AisenHour Logo" 
                            className="h-12 sm:h-18 md:h-24 w-auto"
                        />
                        <button onClick={() => openModal('signin')} className={buttonStyle}>Sign In</button>
                    </nav>
                </header>

                {/* Main content */}
                <main className="flex-grow">
                    {/* Hero Section */}
                    <section className="container mx-auto px-4 py-20 text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">Achieve Your Goals with AI-Powered Guidance</h1>
                        <p className="text-lg md:text-xl mb-8">AisenHour uses advanced AI to create personalized plans and motivating affirmations, helping you reach your goals one hour at a time.</p>
                        <button
                            onClick={() => openModal('register')}
                            className={`${buttonStyle} inline-flex items-center`}
                        >
                            Get Started <ArrowRight className="ml-2" size={20} />
                        </button>
                    </section>

                    {/* Features Section */}
                    <section id="features" className="bg-gray-800 bg-opacity-50 py-20">
                        <div className="container mx-auto px-4">
                            <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
                            <div className="grid md:grid-cols-3 gap-8">
                                {features.map((feature, index) => (
                                    <div key={index} className="bg-gray-700 bg-opacity-50 p-6 rounded-lg">
                                        <div className="text-purple-400 mb-4 flex justify-center">{feature.icon}</div>
                                        <p className="text-center">{feature.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Eisenhower Matrix Section */}
                    <section id="eisenhower-matrix" className="container mx-auto px-4 py-20">
                        <h2 className="text-3xl font-bold mb-12 text-center">The Power of the Eisenhower Matrix</h2>
                        <div className="flex flex-col md:flex-row items-center justify-between">
                            <div className="md:w-1/2 mb-8 md:mb-0">
                                <EisenhowerMatrixSVG />
                            </div>
                            <div className="md:w-1/2 md:pl-8">
                                <h3 className="text-2xl font-semibold mb-4">Why the Eisenhower Matrix?</h3>
                                <ul className="space-y-4">
                                    <li className="flex items-start">
                                        <Check className="mr-2 mt-1 flex-shrink-0 text-green-400" size={20} />
                                        <span>Prioritize tasks based on urgency and importance</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Check className="mr-2 mt-1 flex-shrink-0 text-green-400" size={20} />
                                        <span>Focus on what truly matters, reducing stress and increasing productivity</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Check className="mr-2 mt-1 flex-shrink-0 text-green-400" size={20} />
                                        <span>Make informed decisions about task delegation and elimination</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Check className="mr-2 mt-1 flex-shrink-0 text-green-400" size={20} />
                                        <span>Achieve a better work-life balance by managing your time effectively</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Stats Section */}
                    <section className="bg-gray-800 bg-opacity-50 py-20">
                        <div className="container mx-auto px-4 text-center">
                            <h2 className="text-3xl font-bold mb-8">AisenHour in Action</h2>
                            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
                                <div className="flex items-center">
                                    <Sparkles className="mr-2" size={24} />
                                    <span className="text-lg">{stats.aiNotesGeneratedCount} AI Notes Generated</span>
                                </div>
                                <div className="flex items-center">
                                    <Hourglass className="mr-2" size={24} />
                                    <span className="text-lg">{stats.hourlySuggestionsCount} Hourly Suggestions Provided</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section id="get-started" className="container mx-auto px-4 py-20 text-center">
                        <h2 className="text-3xl font-bold mb-6">Ready to Start Your Journey?</h2>
                        <p className="text-xl mb-8">Sign up now and take the first step towards achieving your goals.</p>
                        <button
                            onClick={() => openModal('register')}
                            className={`${buttonStyle} inline-flex items-center`}
                        >
                            Start Your Productive Hour <ArrowRight className="ml-2" size={20} />
                        </button>
                    </section>
                </main>

                {/* Footer */}
                <footer className="bg-gray-800 bg-opacity-50 py-8">
                    <div className="container mx-auto px-4 text-center text-gray-400">
                        © 2024 AisenHour. All rights reserved.
                    </div>
                </footer>
            </div>

            {showSignInModal && (
                <SignInOverlay
                    onClose={() => setShowSignInModal(false)}
                    onSignIn={onSignIn}
                    onRegister={onRegister}
                    onForgotPassword={onForgotPassword}
                    initialTab={initialTab}
                />
            )}
        </div>
    );
};

export default LandingPage;